<template>
  <div class="login-page">
    <img class="login-page__background" :src="loginBackground" alt="" aria-hidden="true" />
    <div class="login-page__overlay" aria-hidden="true"></div>
    <div class="login-page__glow" aria-hidden="true"></div>

    <main class="login-layout">
      <section class="login-panel" aria-labelledby="login-title">
        <header class="login-panel__header">
          <img class="login-logo" :src="brandLogo" alt="Fusco Fit" />
          <p class="login-kicker">
            <span aria-hidden="true"></span>
            Area riservata
          </p>
          <h1 id="login-title">Accedi al tuo account</h1>
          <p class="login-description">
          Usa le credenziali di Fusco Fit Connect (logo nero).
          </p>
        </header>

        <form class="login-form" @submit.prevent="handleLogin">
          <label class="login-field">
            <span>Email</span>
            <span class="login-field__control">
              <span class="material-icons" aria-hidden="true">mail_outline</span>
              <input
                v-model="email"
                type="email"
                inputmode="email"
                autocomplete="email"
                placeholder="nome@email.it"
                required
              />
            </span>
          </label>

          <label class="login-field">
            <span>Password</span>
            <span class="login-field__control">
              <span class="material-icons" aria-hidden="true">lock_outline</span>
              <input
                v-model="password"
                type="password"
                autocomplete="current-password"
                placeholder="Inserisci la password"
                required
              />
            </span>
          </label>

          <button class="login-submit" type="submit" :disabled="isLoggingIn || isResetting">
            <span>{{ isLoggingIn ? 'Accesso in corso…' : 'Accedi' }}</span>
            <span class="material-icons" aria-hidden="true">arrow_forward</span>
          </button>

          <p
            v-if="errorMessage || resetMessage"
            class="login-message"
            :class="{ 'login-message--error': errorMessage, 'login-message--success': resetMessage && !errorMessage }"
            :role="errorMessage ? 'alert' : 'status'"
            aria-live="polite"
          >
            <span class="material-icons" aria-hidden="true">
              {{ errorMessage ? 'error_outline' : 'check_circle_outline' }}
            </span>
            <span>{{ errorMessage || resetMessage }}</span>
          </p>

          <button class="login-link" type="button" :disabled="isLoggingIn || isResetting" @click="handlePasswordReset">
            {{ isResetting ? 'Invio email in corso…' : 'Hai dimenticato la password?' }}
          </button>

          <div class="login-divider" aria-hidden="true">
            <span></span>
            <small>oppure</small>
            <span></span>
          </div>

          <button class="create-account-button" type="button" :disabled="isLoggingIn || isResetting" @click="goToCreateAccount">
            <span>Crea un nuovo account</span>
            <span class="material-icons" aria-hidden="true">person_add_alt</span>
          </button>
        </form>

        <footer class="login-panel__footer">
          <span class="material-icons" aria-hidden="true">verified_user</span>
          <span>Accesso sicuro Fusco Fit</span>
        </footer>
      </section>
    </main>

    <p class="version">Versione 0000070</p>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useStore } from 'vuex'; // Importa useStore da vuex
import { signInWithEmailAndPassword, sendPasswordResetEmail, signInWithCustomToken } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useRouter, useRoute } from 'vue-router';
import { auth, authPersistenceReady, db } from '@/firebaseConfig';
import loginBackground from '../../fuscofit/assets/hero_coachingday.png';
import brandLogo from '../../fuscofit/assets/logo-white.png';

const withTimeout = (promise, timeoutMs, message, code = 'app/timeout') => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => {
      const error = new Error(message);
      error.code = code;
      reject(error);
    }, timeoutMs);
  });

  return Promise.race([
    Promise.resolve(promise).finally(() => window.clearTimeout(timeoutId)),
    timeoutPromise,
  ]);
};

const getLoginErrorMessage = (error) => {
  if (error?.code === 'app/auth-timeout') {
    return 'Il servizio di accesso non ha risposto. Riprova: non dipende dalla velocità della tua connessione.';
  }

  if (error?.code === 'auth/network-request-failed') {
    return 'Firebase non è raggiungibile in questo momento. Riprova tra qualche secondo.';
  }

  if (
    error?.code === 'auth/invalid-credential' ||
    error?.code === 'auth/wrong-password' ||
    error?.code === 'auth/user-not-found' ||
    error?.code === 'auth/invalid-email'
  ) {
    return 'Email o password errata!';
  }

  return 'Accesso non completato. Riprova tra qualche secondo.';
};

export default {
  setup() {
    const email = ref("");
    const password = ref("");
    const router = useRouter();
    const route = useRoute();
    const store = useStore(); // Utilizza useStore per ottenere l'istanza dello store Vuex
    const errorMessage = ref("");
    const resetMessage = ref("");
    const isLoggingIn = ref(false);
    const isResetting = ref(false);
    const showVideo = ref(false);

    const getDestination = (userRole = 'User') => {
      if (route.query.redirect === '/i-miei-allenamenti') return '/i-miei-allenamenti';
      if (userRole === 'Amministratore' || userRole === 'Studio') return '/dashboard';
      if (userRole === 'Spedizioni') return '/order-list';
      return '/user';
    };

    // Metodo per autenticare l'utente con il token dall'URL
    const authenticateWithToken = async (token) => {
      try {
        await authPersistenceReady;
        // Utilizza il token per autenticare l'utente
        const userCredential = await signInWithCustomToken(auth, token);
        const user = userCredential.user;
        // Qui puoi impostare lo stato dell'utente nell'applicazione
        // Ad esempio, salvare l'UID dell'utente nello store
        store.dispatch('setUserToken', user.uid);
        // Eventualmente reindirizzare l'utente alla pagina corretta
      } catch (error) {
        console.error("Errore durante l'autenticazione con token:", error);
      }
    };


    const goToCreateAccount = () => {
  console.log('Navigazione verso la pagina di creazione account');
  router.push('/createusers');
};



    // Metodo per chiudere il video
    const closeVideo = () => {
      showVideo.value = false;
      errorMessage.value = ""; // Resetta il messaggio di errore
      resetMessage.value = ""; // Resetta il messaggio di reset
    };

    onMounted(async () => {
      // Mostra il video quando il componente è montato
      showVideo.value = true;
      // Estrai il token dai parametri dell'URL
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      if (token) {
        // Se un token è presente, autentica l'utente con quel token
        authenticateWithToken(token);
        return;
      }

      // Una sessione gia valida non deve mostrare di nuovo il login. Con la
      // persistenza localStorage questo controllo nel WebView e immediato.
      try {
        await authPersistenceReady;
        await withTimeout(
          auth.authStateReady(),
          1500,
          'Ripristino sessione non disponibile.',
          'app/session-timeout'
        );
      } catch (sessionError) {
        console.warn('Ripristino sessione saltato:', sessionError);
      }

      if (auth.currentUser && router.currentRoute.value.path === '/') {
        const storedRole = localStorage.getItem('authUid') === auth.currentUser.uid
          ? (localStorage.getItem('userRole') || 'User')
          : 'User';
        router.replace(getDestination(storedRole)).catch(() => {});
      }
    });



    const handleLogin = async () => {
      if (isLoggingIn.value || isResetting.value) return;

      let destination = '/user';
      errorMessage.value = "";
      resetMessage.value = "";
      isLoggingIn.value = true;

      try {
        await authPersistenceReady;
        // signInWithEmailAndPassword restituisce gia l'utente autenticato. Un
        // secondo listener onAuthStateChanged crea un'attesa duplicata e, nei
        // WebView iOS, puo restare accodato alla persistenza precedente.
        const userCredential = await withTimeout(
          signInWithEmailAndPassword(auth, email.value.trim().toLowerCase(), password.value),
          10000,
          'Timeout durante l’autenticazione.',
          'app/auth-timeout'
        );
        const user = userCredential.user;
        if (!user) throw new Error('Utente Firebase non disponibile dopo il login.');

        const isSameStoredUser = localStorage.getItem('authUid') === user.uid;
        let userRole = isSameStoredUser ? (localStorage.getItem('userRole') || 'User') : 'User';

        try {
          const userDoc = await withTimeout(
            getDoc(doc(db, "users", user.uid)),
            2500,
            'Timeout nel caricamento del profilo utente.',
            'app/profile-timeout'
          );

          if (userDoc.exists()) {
            userRole = userDoc.data().userRole || 'User';
          } else {
            console.warn("Documento utente non trovato: continuo come utente standard.");
          }
        } catch (profileError) {
          // Il login Firebase è già riuscito: un ritardo di Firestore non deve
          // lasciare l'utente bloccato nella schermata di accesso.
          console.warn("Profilo utente non disponibile, uso il ruolo di fallback:", profileError);
        }

        localStorage.setItem('authUid', user.uid);
        localStorage.setItem('userRole', userRole);
        await Promise.all([
          store.dispatch('setUserEmail', user.email),
          store.dispatch('setUserRole', userRole),
          store.dispatch('setUserUid', user.uid),
        ]);

        destination = getDestination(userRole);

        await router.replace(destination);

        if (router.currentRoute.value.path !== destination) {
          window.location.hash = `#${destination}`;
        }
      } catch (error) {
        console.error("Errore durante il login:", error);
        if (auth.currentUser) {
          // L'autenticazione è riuscita ma il router non ha completato la
          // transizione: il cambio hash è un fallback sicuro per Capacitor.
          window.location.hash = `#${destination}`;
          return;
        }
        errorMessage.value = getLoginErrorMessage(error);
      } finally {
        isLoggingIn.value = false;
      }
    };





    const handlePasswordReset = async () => {
      if (isResetting.value || isLoggingIn.value) return;

      const normalizedEmail = email.value.trim().toLowerCase();
      errorMessage.value = "";
      resetMessage.value = "";

      if (!normalizedEmail) {
        resetMessage.value = "Inserisci la tua email e poi riclicca qui ";
        return;
      }

      isResetting.value = true;
      try {
        await authPersistenceReady;
        await withTimeout(
          sendPasswordResetEmail(auth, normalizedEmail),
          10000,
          'Timeout durante il recupero password.',
          'app/auth-timeout'
        );
        resetMessage.value = "Email per il ripristino della password inviata";
      } catch (error) {
        console.error("Errore nell'invio dell'email di reset", error);
        errorMessage.value = getLoginErrorMessage(error);
      } finally {
        isResetting.value = false;
      }
    };

    const forceRefresh = () => {
      // Forza il refresh della pagina senza usare la cache
      window.location.reload(true);
    };

    return {
      brandLogo,
      email,
      loginBackground,
      password,
      handleLogin,
      handlePasswordReset,
      errorMessage,
      resetMessage,
      isLoggingIn,
      isResetting,
      showVideo, // Aggiunto alla return
      closeVideo, // Aggiunto alla return
      forceRefresh,
      goToCreateAccount
    };
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Montserrat:ital,wght@0,700;0,800;0,900;1,800;1,900&display=swap');

:global(body) {
  margin: 0;
  background: #07090a;
}

:global(#app) {
  min-height: 100vh;
}

.login-page,
.login-page *,
.login-page *::before,
.login-page *::after {
  box-sizing: border-box;
}

.login-page {
  --login-red: #d50b35;
  --login-red-dark: #a90f29;
  --login-white: #ffffff;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100vw;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: none;
  touch-action: pan-y;
  background: #07090a;
  color: var(--login-white);
  font-family: 'DM Sans', sans-serif;
  isolation: isolate;
}

.login-page__background,
.login-page__overlay,
.login-page__glow {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
}

.login-page__background {
  z-index: -3;
  object-fit: cover;
  object-position: center;
  filter: saturate(0.78) contrast(1.04);
  transform: scale(1.015);
}

.login-page__overlay {
  z-index: -2;
  background:
    linear-gradient(90deg, rgba(4, 6, 7, 0.96) 0%, rgba(4, 6, 7, 0.82) 31%, rgba(4, 6, 7, 0.36) 67%, rgba(4, 6, 7, 0.32) 100%),
    linear-gradient(0deg, rgba(4, 5, 6, 0.7) 0%, transparent 46%, rgba(4, 5, 6, 0.16) 100%);
}

.login-page__glow {
  z-index: -1;
  background:
    radial-gradient(circle at 12% 84%, rgba(213, 11, 53, 0.2), transparent 27rem),
    radial-gradient(circle at 88% 8%, rgba(11, 143, 170, 0.12), transparent 28rem);
  pointer-events: none;
}

.login-layout {
  display: flex;
  align-items: center;
  flex: 1 0 auto;
  width: min(calc(100% - 64px), 1392px);
  min-height: 0;
  margin: 0 auto;
  padding: 34px 0;
}

.login-panel {
  width: min(100%, 460px);
  padding: clamp(28px, 4vw, 42px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 30px;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.115), rgba(255, 255, 255, 0.045)),
    rgba(6, 8, 9, 0.5);
  box-shadow:
    0 34px 90px rgba(0, 0, 0, 0.46),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(30px) saturate(1.25);
  -webkit-backdrop-filter: blur(30px) saturate(1.25);
}

.login-panel__header {
  text-align: left;
}

.login-logo {
  display: block;
  width: 98px;
  height: auto;
  margin: 0 0 27px;
  object-fit: contain;
}

.login-kicker {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 11px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.login-kicker > span {
  width: 27px;
  height: 3px;
  border-radius: 999px;
  background: var(--login-red);
}

.login-panel h1 {
  max-width: 390px;
  margin: 0;
  color: var(--login-white);
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 900;
  line-height: 0.94;
  letter-spacing: -0.06em;
  text-transform: uppercase;
}

.login-description {
  max-width: 370px;
  margin: 17px 0 0;
  color: rgba(255, 255, 255, 0.68);
  font-size: var(--ff-type-body);
  line-height: 1.52;
}

.login-form {
  display: grid;
  gap: 15px;
  width: 100%;
  margin-top: 28px;
}

.login-field {
  display: grid;
  gap: 7px;
  color: rgba(255, 255, 255, 0.76);
  font-size: var(--ff-type-label);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.login-field__control {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  min-height: 54px;
  padding: 0 15px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.09);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.login-field__control:focus-within {
  border-color: rgba(255, 255, 255, 0.54);
  background: rgba(255, 255, 255, 0.13);
  box-shadow:
    0 0 0 3px rgba(213, 11, 53, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.login-field__control .material-icons {
  color: rgba(255, 255, 255, 0.68);
  font-size: 20px;
}

.login-field input {
  width: 100%;
  min-width: 0;
  padding: 15px 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--login-white);
  font: 500 16px/1.2 'DM Sans', sans-serif;
  letter-spacing: normal;
  text-transform: none;
}

.login-field input::placeholder {
  color: rgba(255, 255, 255, 0.38);
  opacity: 1;
}

.login-submit,
.create-account-button,
.login-link {
  appearance: none;
  border: 0;
  font-family: 'DM Sans', sans-serif;
  cursor: pointer;
}

.login-submit,
.create-account-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 54px;
  padding: 0 17px 0 20px;
  border-radius: 16px;
  font-size: 0.9375rem;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.login-submit {
  margin-top: 3px;
  background: linear-gradient(135deg, #e20b39, var(--login-red-dark));
  color: var(--login-white);
  box-shadow: 0 15px 28px rgba(213, 11, 53, 0.28);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.login-submit:hover {
  filter: brightness(1.08);
  box-shadow: 0 18px 34px rgba(213, 11, 53, 0.36);
  transform: translateY(-2px);
}

.login-submit:disabled,
.create-account-button:disabled,
.login-link:disabled {
  cursor: wait;
  opacity: 0.68;
  transform: none;
}

.login-submit .material-icons,
.create-account-button .material-icons {
  color: currentColor;
  font-size: 20px;
}

.login-message {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  margin: 0;
  padding: 11px 12px;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 12px;
  font-size: var(--ff-type-body-small);
  line-height: 1.4;
}

.login-message .material-icons {
  font-size: 18px;
}

.login-message--error {
  border-color: rgba(255, 83, 113, 0.3);
  background: rgba(213, 11, 53, 0.14);
  color: #ffd7df;
}

.login-message--success {
  border-color: rgba(142, 222, 178, 0.25);
  background: rgba(89, 174, 127, 0.13);
  color: #d9ffea;
}

.login-link {
  justify-self: center;
  padding: 2px 4px;
  background: transparent;
  color: rgba(255, 255, 255, 0.72);
  font-size: var(--ff-type-body-small);
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: rgba(255, 255, 255, 0.3);
  text-underline-offset: 4px;
}

.login-link:hover {
  color: var(--login-white);
}

.login-divider {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.38);
}

.login-divider > span {
  height: 1px;
  background: rgba(255, 255, 255, 0.13);
}

.login-divider small {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.create-account-button {
  border: 1px solid rgba(255, 255, 255, 0.17);
  background: rgba(255, 255, 255, 0.1);
  color: var(--login-white);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  transition: border-color 0.2s ease, background 0.2s ease;
}

.create-account-button:hover {
  border-color: rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.15);
}

.login-submit:focus-visible,
.create-account-button:focus-visible,
.login-link:focus-visible,
.login-field input:focus-visible {
  outline: 3px solid rgba(255, 255, 255, 0.52);
  outline-offset: 3px;
}

.login-field input:focus-visible {
  outline: 0;
}

.login-panel__footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.11);
  color: rgba(255, 255, 255, 0.42);
  font-size: var(--ff-type-label);
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.login-panel__footer .material-icons {
  color: rgba(255, 255, 255, 0.52);
  font-size: 16px;
}

.version {
  position: relative;
  align-self: center;
  flex: 0 0 auto;
  margin: 0;
  padding: 0 20px calc(14px + env(safe-area-inset-bottom, 0px));
  color: rgba(255, 255, 255, 0.42);
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

@media (max-width: 640px) {
  .login-page__background {
    object-position: 76% center;
  }

  .login-page__overlay {
    background:
      linear-gradient(0deg, rgba(4, 6, 7, 0.82) 0%, rgba(4, 6, 7, 0.58) 58%, rgba(4, 6, 7, 0.38) 100%),
      linear-gradient(90deg, rgba(4, 6, 7, 0.44), rgba(4, 6, 7, 0.18));
  }

  .login-layout {
    align-items: center;
    width: calc(100% - 24px);
    padding:
      calc(18px + env(safe-area-inset-top, 0px))
      0
      calc(42px + env(safe-area-inset-bottom, 0px));
  }

  .login-panel {
    padding: 25px 20px 21px;
    border-radius: 24px;
    background:
      linear-gradient(145deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.045)),
      rgba(6, 8, 9, 0.47);
  }

  .login-logo {
    width: 82px;
    margin-bottom: 21px;
  }

  .login-panel h1 {
    max-width: 330px;
    font-size: min(clamp(1.9rem, 9vw, 2.35rem), 12vw);
    overflow-wrap: anywhere;
  }

  .login-kicker {
    font-size: min(0.6875rem, 5vw);
  }

  .login-description {
    margin-top: 14px;
    font-size: min(var(--ff-type-body), 6vw);
  }

  .login-form {
    gap: 12px;
    margin-top: 22px;
  }

  .login-field__control,
  .login-submit,
  .create-account-button {
    min-height: 51px;
    border-radius: 15px;
  }

  .login-submit,
  .create-account-button {
    padding-block: 10px;
    font-size: min(0.9375rem, 8vw);
    line-height: 1.2;
  }

  .login-link {
    font-size: min(var(--ff-type-body-small), 8vw);
    line-height: 1.25;
  }

  .login-panel__footer {
    margin-top: 19px;
    padding-top: 15px;
    font-size: min(var(--ff-type-label), 4.5vw);
  }

  .version {
    font-size: min(0.6875rem, 4vw);
    text-align: center;
  }
}

@media (max-height: 720px) and (max-width: 640px) {
  .login-layout {
    align-items: flex-start;
    padding-top: calc(12px + env(safe-area-inset-top, 0px));
  }

  .login-panel {
    padding-top: 20px;
  }

  .login-logo {
    width: 72px;
    margin-bottom: 15px;
  }

  .login-description {
    display: none;
  }

  .login-form {
    margin-top: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-submit,
  .create-account-button,
  .login-field__control {
    transition: none;
  }
}
</style>
